import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';

// ── Vertex shader: bypass camera, fill screen directly ──────────────────────
const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    // planeGeometry(2,2) has verts at ±1 — map directly to clip space
    gl_Position = vec4(position.xy, 0.0, 1.0);
  }
`;

// ── Fragment shader: perspective road with flying lane lines ─────────────────
const fragmentShader = `
  varying vec2 vUv;
  uniform float uTime;
  uniform float uColorShift; // 0 = night navy, 1 = dawn purple

  void main() {
    vec2 uv = vUv; // Three.js plane UVs: (0,0)=bottom-left, (1,1)=top-right

    // ── Colors ──────────────────────────────────────────────────────────────
    vec3 skyLow   = vec3(0.055, 0.090, 0.150);  // near-horizon sky
    vec3 skyHigh  = vec3(0.025, 0.045, 0.085);  // top of sky
    vec3 roadBase = vec3(0.080, 0.108, 0.155);  // asphalt surface
    vec3 terrain  = vec3(0.030, 0.055, 0.085);  // dark roadside
    vec3 white    = vec3(0.920, 0.920, 0.860);  // lane markings
    vec3 accent   = vec3(0.231, 0.510, 0.965);  // #3b82f6
    vec3 dawnSky  = vec3(0.100, 0.040, 0.180);  // Act 2 bg
    vec3 dawnLine = vec3(0.880, 0.280, 0.780);  // Act 2 lines

    float vpY = 0.62; // horizon sits 62% up the screen

    // ── SKY (above horizon) ──────────────────────────────────────────────────
    if (uv.y >= vpY) {
      float s = (uv.y - vpY) / (1.0 - vpY); // 0 at horizon, 1 at top
      vec3 sky = mix(skyLow, skyHigh, s * s);
      sky = mix(sky, mix(dawnSky, dawnSky * 0.4, s), uColorShift);

      // Bright glow strip right at the horizon
      float hg = exp(-pow((uv.y - vpY) * 18.0, 2.0)) * 0.65;
      sky += mix(accent * 0.55, dawnLine * 0.50, uColorShift) * hg;

      gl_FragColor = vec4(clamp(sky, 0.0, 1.0), 1.0);
      return;
    }

    // ── ROAD (below horizon) ─────────────────────────────────────────────────
    float depth     = vpY - uv.y;        // 0 at horizon → vpY at bottom
    float normDepth = depth / vpY;        // 0 at horizon → 1 at bottom

    // Road half-width in UV space.
    // At bottom (normDepth=1): roadHalf=0.5 → road fills full screen width.
    // At horizon (normDepth=0): roadHalf=0 → road vanishes to a point.
    float roadHalf = normDepth * 0.5;

    // Road coordinate: -1 = left edge, 0 = centre, +1 = right edge
    float rf = (uv.x - 0.5) / max(roadHalf, 0.001);

    bool onRoad = abs(rf) < 1.0;

    // Base surface colour
    vec3 col = onRoad ? roadBase : terrain;
    col = mix(col, dawnSky * 0.55, uColorShift);

    if (onRoad) {
      // Perspective-correct dash animation.
      // sqrt(normDepth) compresses dashes near the horizon, spreads them near camera.
      float t         = sqrt(normDepth);
      float dashAnim  = mod(t * 7.0 - uTime * 2.5, 1.0);
      float dash      = step(0.52, dashAnim); // ~48% of tile is solid line

      // Line half-width in road-fraction space (constant real-world width)
      float lw = 0.055;

      // Centre dashed white line
      float cLine = smoothstep(lw, lw * 0.2, abs(rf)) * dash;

      // Solid white edge lines (sitting just inside the road boundary)
      float ew   = lw * 0.75;
      float edge = smoothstep(ew, ew * 0.15, abs(abs(rf) - 0.88));

      // Dashed lane dividers at ±0.44 road fraction
      float dw  = lw * 0.65;
      float div = smoothstep(dw, dw * 0.15, abs(abs(rf) - 0.44)) * dash * 0.62;

      float lines = clamp(cLine + edge + div, 0.0, 1.0);

      // Line colour (white → dawn-pink in Act 2)
      vec3 lCol = mix(white, dawnLine, uColorShift * 0.55);

      // Subtle centre-road glow (headlights / lane glow)
      float glow = exp(-abs(rf) * 2.2) * normDepth * 0.22;
      vec3 gCol  = mix(accent * 0.75, dawnLine * 0.55, uColorShift);

      col = col + lCol * lines * 0.88 + gCol * glow;
    }

    // Fog: cubic falloff blending road/terrain to horizon sky near the top
    float fog    = (1.0 - normDepth);
    fog          = fog * fog * fog;
    vec3 fogCol  = mix(skyLow * 0.65, dawnSky * 0.55, uColorShift);
    col          = mix(col, fogCol, fog * 0.80);

    // Vignette
    vec2  vc   = uv - 0.5;
    float vign = 1.0 - dot(vc, vc) * 1.15;
    col *= clamp(vign, 0.35, 1.0);

    gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  }
`;

// ── Inner scene component ────────────────────────────────────────────────────
function ShaderPlane({ colorShiftRef }) {
  const uniforms = useMemo(() => ({
    uTime:       { value: 0 },
    uColorShift: { value: 0 },
  }), []);

  useFrame(({ clock }) => {
    uniforms.uTime.value       = clock.getElapsedTime();
    uniforms.uColorShift.value = colorShiftRef.current;
  });

  return (
    <mesh>
      <planeGeometry args={[2, 2]} />
      <shaderMaterial
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        depthTest={false}
        depthWrite={false}
      />
    </mesh>
  );
}

// ── Public component ─────────────────────────────────────────────────────────
// colorShiftRef: a React ref whose .current is a 0–1 float.
// Using a ref (not state) lets GSAP update it without React re-renders.
export default function RoadShader({ colorShiftRef }) {
  const fallbackRef = useRef({ current: 0 });
  const ref = colorShiftRef || fallbackRef;

  // Respect prefers-reduced-motion: render a static gradient instead
  const prefersReduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReduced) {
    return (
      <div style={{
        position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
        background: 'linear-gradient(180deg, #0a1020 0%, #0d1a2a 100%)',
      }} />
    );
  }

  return (
    <Canvas
      style={{
        position: 'fixed',
        top: 0, left: 0,
        width: '100vw', height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
      }}
      gl={{
        antialias: false,
        powerPreference: 'low-power',
        alpha: false,
      }}
      frameloop="always"
      // Orthographic-ish: we bypass camera in vertex shader anyway
      camera={{ position: [0, 0, 1], near: 0.1, far: 10 }}
    >
      <ShaderPlane colorShiftRef={ref} />
    </Canvas>
  );
}
